import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, School, ShoppingBag, TreePine, Users, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Header } from '@/components/layout/Header';
import { TownNavBar } from '@/components/layout/TownNavBar';
import { AreaNavBar } from '@/components/layout/AreaNavBar';
import { Footer } from '@/components/layout/Footer';
import { RealtorCard } from '@/components/realtor/RealtorCard';
import { OfferLeadForm } from '@/components/forms/OfferLeadForm';
import { supabase } from '@/integrations/supabase/client';
import { gaEvents, useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { PublicThemeProvider } from '@/components/theme/ThemeProvider';

interface SiteData {
  id: string;
  name: string;
  location_served: string;
  theme: string;
  favicon_url: string | null;
  tenant_id: string | null;
  canonical_base_url: string | null;
  header_font: string | null;
  enable_market_value_report?: boolean;
  enable_community_guide?: boolean;
  enable_mortgage_info?: boolean;
  realtor_assigned?: boolean;
}

interface TownData {
  id: string;
  name: string;
  slug: string;
}

interface RealtorData {
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  logo_url: string | null;
  title_preference?: string | null;
}

export default function CommunityGuidePage() {
  const navigate = useNavigate();
  const [site, setSite] = useState<SiteData | null>(null);
  const [towns, setTowns] = useState<TownData[]>([]);
  const [areas, setAreas] = useState<{ id: string; name: string; slug: string }[]>([]);
  const [realtor, setRealtor] = useState<RealtorData | null>(null);
  const [loading, setLoading] = useState(true);

  useGoogleAnalytics(site?.tenant_id || null);

  useEffect(() => {
    fetchSiteData();
  }, []);

  // Track offer page view
  useEffect(() => {
    if (site && !loading) {
      gaEvents.viewOffer('community_guide', '/community-guide');
    }
  }, [site, loading]);

  const fetchSiteData = async () => {
    setLoading(true);
    try {
      const hostname = window.location.hostname;
      const isDevelopment = hostname === 'localhost' || hostname.includes('lovable.app');
      
      let siteData: SiteData | null = null;

      if (!isDevelopment) {
        const hostWithoutWww = hostname.replace(/^www\./, '');
        const { data } = await supabase
          .from('sites_public' as any)
          .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
          .eq('status', 'active')
          .or(`primary_domain.eq.${hostname},primary_domain.eq.${hostWithoutWww}`)
          .limit(1)
          .maybeSingle();
        siteData = data as unknown as SiteData;
      }

      if (!siteData && isDevelopment) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: siteAdmin } = await supabase
            .from('site_admins')
            .select('site_id')
            .eq('user_id', user.id)
            .single();

          if (siteAdmin) {
            const { data } = await supabase
              .from('sites_public' as any)
              .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
              .eq('id', siteAdmin.site_id)
              .maybeSingle();
            siteData = data as unknown as SiteData;
          }
        }
      }

      if (!siteData) {
        const { data } = await supabase
          .from('sites_public' as any)
          .select('id, name, location_served, theme, favicon_url, tenant_id, canonical_base_url, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, realtor_assigned')
          .eq('status', 'active')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        siteData = data as unknown as SiteData;
      }

      if (siteData) {
        // Redirect if feature is disabled
        if (siteData.enable_community_guide === false) {
          navigate('/', { replace: true });
          return;
        }
        
        setSite(siteData);

        // Fetch towns
        const { data: townsData } = await supabase
          .from('towns')
          .select('id, name, slug')
          .eq('site_id', siteData.id)
          .eq('is_published', true)
          .order('name');
        setTowns(townsData || []);
        
        // If only one town, fetch its areas for the AreaNavBar
        if (townsData && townsData.length === 1) {
          const { data: areasData } = await supabase
            .from('areas')
            .select('id, name, slug')
            .eq('town_id', townsData[0].id)
            .eq('is_published', true)
            .order('name');
          
          if (areasData) setAreas(areasData);
        }

        // Fetch realtor
        const { data: realtorData } = await supabase
          .from('realtor_profiles')
          .select('first_name, last_name, photo_url, phone, city, state, license_numbers, about_me, logo_url, title_preference, ad_image_url')
          .eq('site_id', siteData.id)
          .single();
        setRealtor(realtorData);
      }
    } catch (error) {
      console.error('Error fetching site data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading...</title>
          <meta name="description" content="" />
          <meta property="og:title" content="" />
          <meta property="og:description" content="" />
          <meta name="twitter:title" content="" />
          <meta name="twitter:description" content="" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </>
    );
  }

  if (!site) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Site not found</p>
      </div>
    );
  }

  const showRealtorContent = site.realtor_assigned !== false;
  const realtorName = showRealtorContent && realtor?.first_name && realtor?.last_name
    ? `${realtor.first_name} ${realtor.last_name}`
    : null;

  const canonicalUrl = site.canonical_base_url 
    ? `${site.canonical_base_url}/community-guide`
    : null;

  const navTowns = towns.map(t => ({ id: t.id, name: t.name, slug: t.slug }));

  const guideContents = [
    { icon: MapPin, title: 'Neighborhood Profiles', description: 'Detailed information about each community including character, walkability, and lifestyle fit.' },
    { icon: School, title: 'School Information', description: 'Overview of public and private schools, ratings, and what families need to know.' },
    { icon: ShoppingBag, title: 'Local Amenities', description: 'Shopping, dining, healthcare, and entertainment options in each area.' },
    { icon: TreePine, title: 'Parks & Recreation', description: 'Outdoor spaces, trails, beaches, and recreational facilities nearby.' },
    { icon: Users, title: 'Community Events', description: 'Annual festivals, farmers markets, and community gatherings that define local life.' },
  ];

  return (
    <PublicThemeProvider themeId={site.theme} headerFontId={site.header_font}>
      <div className="min-h-screen bg-background flex flex-col">
        <Helmet>
          <title>Free Community Guide | {site.name}</title>
          <meta name="description" content={`Download your free Local Buyer Community Guide for ${site.location_served}. Everything you need to know about neighborhoods, schools, and local amenities.`} />
          {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
          
          {/* Open Graph */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content={site.name} />
          <meta property="og:title" content={`Free Community Guide | ${site.name}`} />
          <meta property="og:description" content={`Download your free Local Buyer Community Guide for ${site.location_served}. Everything you need to know about neighborhoods, schools, and local amenities.`} />
          {/* og:url and og:image are injected by Cloudflare worker */}
          
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`Free Community Guide | ${site.name}`} />
          <meta name="twitter:description" content={`Download your free Local Buyer Community Guide for ${site.location_served}.`} />
          
          {/* Additional SEO */}
          <meta name="robots" content="index, follow" />
        </Helmet>

        <Header 
          siteName={site.name} 
          faviconUrl={site.favicon_url}
          logoUrl={realtor?.logo_url}
          showAdmin={false}
          enableMarketValueReport={site.enable_market_value_report !== false}
          enableCommunityGuide={site.enable_community_guide !== false}
          enableMortgageInfo={site.enable_mortgage_info !== false}
        />

        <TownNavBar 
          towns={navTowns}
          currentTownSlug=""
          enableMarketValueReport={site.enable_market_value_report !== false}
          enableMortgageInfo={site.enable_mortgage_info !== false}
          realtorFirstName={realtor?.first_name}
          themeId={site.theme}
        />
        
        {/* Area Navigation Bar - Show when only one town exists */}
        {towns.length === 1 && areas.length > 0 && (
          <AreaNavBar areas={areas} townSlug={towns[0].slug} />
        )}

        <main className="flex-1">
          {/* Hero Section */}
          <section className="bg-gradient-hero text-white py-16 md:py-24">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-3xl md:text-5xl font-display font-bold mb-4">
                Ready to Find Your Next Home?
              </h1>
              <p className="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto">
                Download our free Local Buyer Community Guide for {site.location_served} - your insider's look at the best places to live.
              </p>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-12 md:py-16">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
                {/* Left: Content */}
                <div className="lg:col-span-2 space-y-8">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">
                      What's Inside Your Free Guide:
                    </h2>
                    <p className="text-muted-foreground mb-8">
                      Moving to a new area? Our comprehensive community guide gives you the local knowledge 
                      you need to choose the right neighborhood for your lifestyle and budget.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {guideContents.map((item, index) => (
                      <Card key={index} className="bg-muted/50">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="p-2 rounded-full bg-primary/10 shrink-0">
                              <item.icon className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold mb-1">{item.title}</h3>
                              <p className="text-sm text-muted-foreground">{item.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <div className="bg-secondary/30 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      Local Expertise, Free to You
                    </h3>
                    <p className="text-muted-foreground">
                      {realtorName || 'Your local agent'} has put together this guide based on years of experience 
                      helping buyers find their perfect home in {site.location_served}. Get the insider knowledge 
                      that will help you make a confident decision about where to live.
                    </p>
                  </div>
                </div>

                {/* Right: Form + Realtor */}
                <div className="space-y-6">
                  <Card className="p-6 lg:p-8 sticky top-8">
                    <h3 className="text-2xl font-display font-bold mb-2 text-center">
                      Get Your Free Guide
                    </h3>
                    <p className="text-muted-foreground text-center mb-6">
                      Enter your info below and {realtorName || 'we'}'ll send your community guide right away.
                    </p>
                    <OfferLeadForm 
                      siteId={site.id}
                      requestType="community_guide"
                      pagePath="/community-guide"
                      submitButtonText="Send My Free Guide"
                      siteDomain={window.location.hostname}
                    />
                  </Card>

                  {showRealtorContent && realtor && (
                    <RealtorCard realtor={realtor} />
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer 
          siteName={site.name} 
          locationServed={site.location_served}
          towns={navTowns}
          areas={areas}
          realtor={showRealtorContent ? realtor : null}
          enableBuyers={true}
          enableSellers={true}
          enableMortgageInfo={site.enable_mortgage_info !== false}
        />
      </div>
    </PublicThemeProvider>
  );
}
